import { LoanProgram, AdjustmentType } from '@frontend/pricing-search';
import { formatDate, lookupKeyFromValue } from '@frontend/utils';
import { updateBuySideAdjustmentsAction } from '../../data/origin/actionCreators';
import api from '../utils/api';
import Common from './Common';
import {
  convertEPPSAdjustmentsToLNCT,
  createAdjustments,
  eppsToLNCTAdjustmentType,
} from './lockConfirmServices';
import Session, {
  ALL_SUMMARY_PARAMS,
  APPLICATION_NAME,
  IS_LO,
  JWT_TOKEN,
  ORIGIN_SOURCE,
  USER_TYPE,
} from './Session';
import {
  extractApprovedReason,
  mapTempApprovedReasonToCorrectAbbreviation,
} from '../../view/components/PricingGrid/SlidePriceCards/helpers';
import { RequestActionType } from '../constants/enumTypes';

interface BuysideAdjustment {
  description: string;
  adjustmentType: string;
  rate?: number;
  price?: number;
  priceAdjustmentType?: string;
  priceAdjustmentTypeIndex?: number;
  status?: string;
  approvalDate?: string;
  approvedBy?: string;
  approvedReason?: string;
  uuid?: string;
  id?: string;
  rateLockAdjustmentType?: string;
}

let hasMappedInitial = false;

export const rateSelector = async (
  dispatch,
  loanData,
  buysideAdjustments,
  loanPrograms?: LoanProgram[],
  allAttrCategories?: any[],
  isScenarioFlow = false,
) => {
  const {
    requestLockStatus,
    requestAction,
    loanInformation,
    relockRequest,
    eppsUserName,
  } = loanData || {};
  const { loanId: loanIdLoan, lienPosition } = loanInformation || {};
  const {
    specificRateDataID: rateDataIdLoan,
    specificProgramID: programIdLoan,
    effectiveDate: effectiveDateLoan,
  } = relockRequest || {};
  const isLOCmode = Session.get(IS_LO);
  const selectedProductData = Session.get(ALL_SUMMARY_PARAMS);
  const {
    loanId,
    rateDataId,
    rateDate,
    programId,
    effectiveDate,
    lockEffectiveDate,
    wcpPolicyApplied,
  } = selectedProductData || {};
  const applicationName = isLOCmode
    ? Session.get(APPLICATION_NAME) || 'ENCW'
    : 'EPPS';

  let loanEffectiveDate = relockRequest?.effectiveDate ?? null;
  let loanUseCurrentDateAsEffectiveDate = false;
  const rateDataID = rateDataId || rateDataIdLoan;
  const matchingRateData = (loanPrograms ?? [])
    .map((program) => program.rateDetails)
    .flat(1)
    .find((rateDetail) => rateDetail.rateDataID === rateDataID);
  const updateLockDate = matchingRateData?.updateLockDate;
  if (wcpPolicyApplied) {
    loanEffectiveDate = matchingRateData?.rateDate;
    loanUseCurrentDateAsEffectiveDate =
      !lockEffectiveDate && !loanEffectiveDate;
  } else {
    loanEffectiveDate = loanEffectiveDate || lockEffectiveDate || null;
  }
  const body = {
    loanId: loanId || loanIdLoan,
    sourceApplicationName: applicationName,
    sourceApplicationFormName: Session.get(ORIGIN_SOURCE) || 'lockrequest',
    requestLockStatus,
    requestAction,
    eppsUserName,
    programId: programId || programIdLoan,
    rateDataID,
    lienPosition: lienPosition || 1,
    effectiveDate: loanEffectiveDate,
    useCurrentDateAsEffectiveDate: loanUseCurrentDateAsEffectiveDate,
    updateLockDate,
  };
  if (isScenarioFlow) {
    body.requestAction = 1;
    body.loanChannel = loanInformation.loanChannel;
  }

  let details = {};
  const user = Session.get(USER_TYPE);
  if (user && user.userType === 'StandAlone') {
    details = await api.selectProgramRate(
      {
        body: JSON.stringify(body),
        customToken: { value: Session.get(JWT_TOKEN) },
        tokenType: 'Bearer',
        loanId,
      },
      {},
    );

    return details;
  }

  // eslint-disable-next-line prefer-const
  let [eppsDetails, loancatcherAdjustments] = await Promise.all([
    await api.getWebhookData(
      {
        headers: { 'X-Elli-PAT': await Common.getPATToken() },
        body: JSON.stringify(body),
      },
      await Common.getOriginId(),
      'LockRates',
    ),
    isScenarioFlow ? [] : await getLoanCatcherAdjustments(loanInformation),
  ]);
  // only map adjustments on initial load or when navigating from buyside to sellside and vice versa.
  if (
    !hasMappedInitial ||
    requestAction === RequestActionType.SellRequest ||
    requestAction === RequestActionType.BuyRequest
  ) {
    processLoancatcherAdjustments(
      dispatch,
      loanData,
      buysideAdjustments,
      loancatcherAdjustments,
      allAttrCategories,
    );
  }
  hasMappedInitial = true;

  return eppsDetails;
};

export const processLoancatcherAdjustments = (
  dispatch,
  loanData,
  buysideAdjustments,
  loancatcherAdjustments,
  allAttrCategories?: any[],
) => {
  const flattenedBuysideAdjustments = (
    Object.values(buysideAdjustments ?? {}) as BuysideAdjustment[][]
  )
    .flat(1)
    .map((adj: BuysideAdjustment) => {
      if (
        (adj?.adjustmentType === 'Adjustment' ||
          adj?.adjustmentType === 'CustomPriceAdjustment') &&
        adj?.description?.includes('-')
      ) {
        // Grab relevant description for adjustments with attribution categories
        adj.description =
          adj?.description?.split('-')?.pop()?.trim() || adj?.description;
      }
      return adj;
    }) as BuysideAdjustment[];
  const isArrayFlat = Array.isArray(buysideAdjustments);
  if (
    loancatcherAdjustments?.length > 0 &&
    flattenedBuysideAdjustments?.length > 0
  ) {
    loancatcherAdjustments.forEach((lcAdjustment) => {
      const matchingBuysideAdjustments = flattenedBuysideAdjustments.filter(
        (bsAdjustment) =>
          bsAdjustment.description === lcAdjustment.description &&
          (bsAdjustment.adjustmentType === 'Adjustment' ||
            bsAdjustment.adjustmentType === 'CustomPriceAdjustment'),
      );
      matchingBuysideAdjustments.forEach((matchingBuyside) => {
        // Special mapping logic for New Lock and Lock Update actions
        if (
          (loanData.requestAction === 2 && !isArrayFlat) ||
          (loanData.requestAction === 1 && hasMappedInitial)
        ) {
          if (matchingBuyside.adjustmentType === 'CustomPriceAdjustment') {
            lcAdjustment.price = matchingBuyside.price;
            lcAdjustment.rate = matchingBuyside.rate;
          }
        } else if (matchingBuyside.adjustmentType === 'Adjustment') {
          lcAdjustment.rate = matchingBuyside.rate;
        } else if (
          matchingBuyside.adjustmentType === 'CustomPriceAdjustment' &&
          !matchingBuyside.uuid &&
          !matchingBuyside.id
        ) {
          lcAdjustment.price = matchingBuyside.rate;
        }
      });
    });
  }
  hasMappedInitial = true;
  const parsedEPPSAdjustmentGroups = loancatcherAdjustments.reduce(
    (sum, curr) => {
      const adjustmentType = lookupKeyFromValue(
        eppsToLNCTAdjustmentType,
        curr.adjustmentType,
      );

      if (!adjustmentType) {
        return sum;
      }

      return {
        ...sum,
        [adjustmentType]: (sum[adjustmentType] ?? []).concat({
          ...curr,
          adjustmentType,
        }),
      };
    },
    {},
  );
  let mappedBuySideAdjustments: any[] | any = buysideAdjustments
    ? Array.isArray(buysideAdjustments)
      ? [...buysideAdjustments]
      : [...[].concat(...(Object.values(buysideAdjustments) as any[]))]
    : [];

  const LockExtensionAdjustment = (mappedBuySideAdjustments ?? []).filter(
    (adj) =>
      adj.adjustmentType === 'LockExtensionAdjustment' ||
      adj.adjustmentType === 'LOCK_EXTENSION_ADJUSTMENT',
  );
  const ReLockFeeAdjustment = (mappedBuySideAdjustments ?? [])
    .filter(
      (adj) =>
        adj.adjustmentType === 'ReLockFeeAdjustment' ||
        adj.adjustmentType === 'RE_LOCK_FEE_ADJUSTMENT',
    )
    .map((adj) => {
      if (adj.description?.includes('-')) {
        // Grab relevant description for adjustments with attribution categories
        adj.description = adj.description.split('-').pop().trim();
      }
      return adj;
    });
  const CustomPriceAdjustment = (mappedBuySideAdjustments ?? [])
    .filter(
      (adj) =>
        adj.adjustmentType === 'CustomPriceAdjustment' ||
        adj.adjustmentType === 'CUSTOM_PRICE_ADJUSTMENT',
    )
    .map((adj) => {
      if (adj.description?.includes('-')) {
        // Grab relevant description for adjustments with attribution categories
        adj.description = adj.description.split('-').pop().trim();
      }
      return adj;
    });
  const CorporatePriceConcession = (mappedBuySideAdjustments ?? []).filter(
    (adj) =>
      adj.adjustmentType === 'CorporatePriceConcession' ||
      adj.adjustmentType === 'CORPORATE_PRICE_CONCESSION',
  );
  const BranchPriceConcession = (mappedBuySideAdjustments ?? []).filter(
    (adj) =>
      adj.adjustmentType === 'BranchPriceConcession' ||
      adj.adjustmentType === 'BRANCH_PRICE_CONCESSION',
  );

  mappedBuySideAdjustments = {
    LockExtensionAdjustment,
    ReLockFeeAdjustment,
    CustomPriceAdjustment,
    CorporatePriceConcession,
    BranchPriceConcession,
  };
  const adjustmentGroups = { ...parsedEPPSAdjustmentGroups };
  const linkEppsAdjustmentData = (adjustment) => {
    const matchedAdjustment = (
      parsedEPPSAdjustmentGroups[adjustment.adjustmentType] || []
    ).find((item, index: number) => {
      // custom price adjustments use rate field for price
      const propTouse =
        item.adjustmentType === AdjustmentType.CustomPriceAdjustment
          ? 'rate'
          : 'price';

      if (
        item.adjustmentType === AdjustmentType.ReLockFeeAdjustment ||
        item.adjustmentType === AdjustmentType.CustomPriceAdjustment
      ) {
        if (
          item.description === adjustment.description &&
          item.price === adjustment[propTouse]
        ) {
          return item;
        }
      }

      if (
        item.adjustmentType === AdjustmentType.CorporatePriceConcession ||
        item.adjustmentType === AdjustmentType.BranchPriceConcession
      ) {
        if (
          item.approvedReason === adjustment.approvedReason &&
          item.price === adjustment.price
        ) {
          return item;
        }
      }

      return null;
    });
    if (matchedAdjustment) {
      adjustment.adjustmentReasonGuid = matchedAdjustment.adjustmentReasonGuid;
      adjustment.paidByGuid = matchedAdjustment.paidByGuid;
      if (
        adjustment.adjustmentType === AdjustmentType.CorporatePriceConcession ||
        adjustment.adjustmentType === AdjustmentType.BranchPriceConcession
      ) {
        adjustment.approvedReason = extractApprovedReason(
          matchedAdjustment.approvedReason,
          matchedAdjustment.paidByGuid,
          matchedAdjustment.adjustmentReasonGuid,
        );
        // this will map the temp approved reason to the correct abbreviation
        // incase the adjustment abbreviation has changed in the backend - EPPS-96118
        adjustment.tempApprovedReason =
          mapTempApprovedReasonToCorrectAbbreviation(
            matchedAdjustment.approvedReason,
            matchedAdjustment.paidByGuid,
            matchedAdjustment.adjustmentReasonGuid,
            allAttrCategories,
          );
      }
    }
    return adjustment;
  };
  const reconcileEppsAndEPCAdjustmentData = (adj, isCustom?: boolean) => {
    if (!isCustom) {
      // These are modeled incorrectly
      // Fixing it here as we are end of release cycle
      // This should be fixed permanently when we fully move to EPC models in Partner UI
      adj.price = adj.rate ?? adj.price;
      delete adj.rate;
    }
    return linkEppsAdjustmentData(adj);
  };
  adjustmentGroups.LockExtensionAdjustment = (
    mappedBuySideAdjustments?.LockExtensionAdjustment ?? []
  ).map((adj) => reconcileEppsAndEPCAdjustmentData(adj));
  adjustmentGroups.ReLockFeeAdjustment = (
    mappedBuySideAdjustments?.ReLockFeeAdjustment ?? []
  ).map((adj) => reconcileEppsAndEPCAdjustmentData(adj));
  // Only map concessions and custom adjustments if there are no loancatcher adjustments
  // When loancatcher has data, preserve it instead of overwriting with EPPS data
  // which may lack concession-specific fields (approvedReason, paidByGuid, etc.)
  if (!loancatcherAdjustments?.length) {
    adjustmentGroups.CorporatePriceConcession = (
      mappedBuySideAdjustments?.CorporatePriceConcession ?? []
    ).map((adj) => reconcileEppsAndEPCAdjustmentData(adj));
    adjustmentGroups.BranchPriceConcession = (
      mappedBuySideAdjustments?.BranchPriceConcession ?? []
    ).map((adj) => reconcileEppsAndEPCAdjustmentData(adj));
    adjustmentGroups.CustomPriceAdjustment = (
      mappedBuySideAdjustments?.CustomPriceAdjustment ?? []
    ).map((adj) => reconcileEppsAndEPCAdjustmentData(adj, true));
  }
  // Remove status from custom adjustments and concessions
  // so fields are not locked as read-only when resubmitting
  [
    'CustomPriceAdjustment',
    'CorporatePriceConcession',
    'BranchPriceConcession',
  ].forEach((type) => {
    adjustmentGroups?.[type]?.forEach((adj) => {
      delete adj.status;
    });
  });

  // Strip abbreviation prefixes from concession approvedReason so Redux
  // always holds clean user input. Prefixes are redundant (same info in
  // paidByGuid/adjustmentReasonGuid) and rebuilt in tempApprovedReason
  // at submission time. This matches the original behavior before the
  // loancatcher guard was added (linkEppsAdjustmentData used to do this).
  ['CorporatePriceConcession', 'BranchPriceConcession'].forEach((type) => {
    adjustmentGroups[type]?.forEach((adj) => {
      if (adj.approvedReason?.includes('-') && (adj.paidByGuid || adj.adjustmentReasonGuid)) {
        adj.approvedReason = extractApprovedReason(
          adj.approvedReason,
          adj.paidByGuid,
          adj.adjustmentReasonGuid,
        );
      }
    });
  });

  const parsedLoancatcherAdjustments = convertEPPSAdjustmentsToLNCT(
    Object.values(adjustmentGroups ?? {}).flat(),
  );

  if (
    !Object.values(adjustmentGroups).length &&
    Object.values(parsedLoancatcherAdjustments).length !==
      Object.values(adjustmentGroups).length
  ) {
    createInitialLoanCatcherAdjustmentsForLoansInProgressFor243({
      loanData,
      parsedLoancatcherAdjustments,
      buysideAdjustments,
      dispatch,
    });
  } else {
    // eslint-disable-next-line no-restricted-syntax
    for (const [type, adjustments] of Object.entries(adjustmentGroups)) {
      dispatch(updateBuySideAdjustmentsAction(adjustments, type));
    }
  }
};

const createInitialLoanCatcherAdjustmentsForLoansInProgressFor243 = ({
  loanData,
  parsedLoancatcherAdjustments,
  buysideAdjustments,
  dispatch,
}) => {
  // Skip await here. This can send the adjustments in the background. We don't need to wait for it to finish.
  createAdjustments(loanData, parsedLoancatcherAdjustments);

  const flattenedBuysideAdjustments = Array.isArray(buysideAdjustments)
    ? buysideAdjustments
    : Object.values(buysideAdjustments ?? {}).flat(1);
  // eslint-disable-next-line no-restricted-syntax
  const initialBuysideAdjustments = flattenedBuysideAdjustments.reduce(
    (sum, _curr) => {
      const curr = { ..._curr };
      // eslint-disable-next-line prefer-destructuring
      const adjustmentType = curr.adjustmentType;
      if (!adjustmentType) {
        return sum;
      }

      curr.price = curr.rate;
      delete curr.rate;
      curr.status = 'Submitted';

      if (curr.approvalDate) {
        curr.approvalDate = formatDate(new Date(curr.approvalDate), 'Calendar');
      }

      return {
        ...sum,
        [adjustmentType]: (sum[adjustmentType] ?? []).concat(curr),
      };
    },
    {},
  );
  // eslint-disable-next-line no-restricted-syntax
  for (const [type, adjustments] of Object.entries(initialBuysideAdjustments)) {
    dispatch(updateBuySideAdjustmentsAction(adjustments, type));
  }
};

export const getLoanCatcherAdjustments = (loanInformation) => {
  return api.getAdjustments({
    loanNumber: loanInformation?.loanId,
  });
};

export const deleteAdjustments = (loanInformation, adjustments) => {
  return api.deleteAdjustments({
    adjustments,
    loanNumber: loanInformation?.loanNumber,
  });
};

export const validateRateDocInstance = async (rateDocInstanceID) => {
  let result;
  if (Session.get(IS_LO)) {
    const patToken = await Common.getPATToken();
    const originId = await Common.getOriginId();
    result = await api.getRateDocInstanceV1(
      { patToken, originId },
      rateDocInstanceID,
    );
  } else {
    const jwtToken = await Session.get(JWT_TOKEN);
    result = await api.getRateDocInstanceV2({ jwtToken }, rateDocInstanceID);
  }
  const jsonData = await result.json();
  return result.ok && jsonData.status === 1;
};

export const getRateSheetDocument = async (rateDocInstanceID) => {
  let result;
  if (Session.get(IS_LO)) {
    const patToken = await Common.getPATToken();
    const originId = await Common.getOriginId();
    result = await api.getRateSheetDocumentV1(
      { patToken, originId },
      rateDocInstanceID,
    );
  } else {
    const jwtToken = await Session.get(JWT_TOKEN);
    result = await api.getRateSheetDocumentV2({ jwtToken }, rateDocInstanceID);
  }
  if (result.ok) {
    const blob = await result.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', 'ratesheet');
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild?.(link);
  }
  return result;
};

export const getEncompassSettings = async () => {
  let result: any | null = null;
  if (Session.get(IS_LO)) {
    result = await api.getEncompassSettingsV1();
  }
  return result;
};
